import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCourses, useUpcomingBatches, type Course } from "@/hooks/useCourses";
import { useCourseCategories, withParamotorTrike, PARAMOTOR_TRIKE_CARD, type CourseCategoryCard } from "@/hooks/useCourseCategories";
import { useFeaturedFeedback } from "@/hooks/useFeedback";
import type { FeedbackRow } from "@/services/feedbackService";
import { useSEO } from "@/hooks/useSEO";
import {
  testimonials,
  stats,
} from "@/mocks/siteData";
import Logo from "@/components/base/Logo";
import OptimizedImage from "@/components/base/OptimizedImage";

function HomeSchemaJSON() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "National Institute of Mountaineering and Adventure Sports (NIMAS)",
    "alternateName": "NIMAS Dirang",
    "url": "https://nimasdirang.com",
    "logo": "https://nimasdirang.com/logo.png",
    "description": "India's premier national institute for mountaineering and adventure sports training under the Ministry of Defence, Government of India. World-class courses across Land, Air and Aqua verticals in the Eastern Himalayas.",
    "foundingDate": "2013",
    "founders": [{
      "@type": "Person",
      "name": "Ministry of Defence, Government of India"
    }],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Dirang",
      "addressLocality": "Dirang",
      "addressRegion": "Arunachal Pradesh",
      "postalCode": "790101",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-9774231855",
      "contactType": "customer service",
      "email": "info@nimasdirang.com",
      "availableLanguage": ["English", "Hindi"]
    },
    "sameAs": [
      "https://www.instagram.com/nimas_dirang",
      "https://www.facebook.com/nimasdirang",
      "https://www.youtube.com/@nimasdirang"
    ],
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "27.3522",
      "longitude": "92.3667"
    },
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "parentOrganization": {
      "@type": "Organization",
      "name": "Ministry of Defence, Government of India"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(el);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function SectionTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-xs font-semibold text-primary tracking-[0.2em] uppercase mb-5 bg-primary/5 px-4 py-1.5 rounded-full">
      {children}
    </span>
  );
}

function HeroSection() {
  const [hoveredStat, setHoveredStat] = useState<number | null>(null);

  return (
    <section className="relative min-h-screen flex items-center bg-white overflow-hidden pt-16">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-primary/5 via-primary/3 to-transparent rounded-full translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-secondary/3 via-secondary/2 to-transparent rounded-full -translate-x-1/4 translate-y-1/3" />
        <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-primary/30 rounded-full -translate-x-1/2 -translate-y-1/2 animate-ping" style={{ animationDuration: "3s" }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 py-10 lg:py-14 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
          <div className="flex-1 text-center lg:text-left max-w-2xl">
            <AnimatedSection delay={0.05}>
              <div className="flex flex-nowrap items-center justify-center lg:justify-start gap-2 sm:gap-3 md:gap-4 mb-6">
                {[
                  {
                    src: "/images/generated/compressed_WhatsApp-Image-2026-07-18-at-11.45.30-AM.webp",
                    alt: "NIMAS Dirang",
                  },
                  {
                    src: "/images/ministry-of-defence-logo.svg",
                    alt: "Ministry of Defence, Government of India",
                  },
                  {
                    src: "/images/generated/compressed_WhatsApp-Image-2026-07-18-at-11.45.31-AM-1.webp",
                    alt: "Fit India",
                  },
                  {
                    src: "/images/generated/compressed_WhatsApp-Image-2026-07-18-at-11.45.31-AM-2.webp",
                    alt: "Viksit Bharat Abhiyan",
                  },
                  {
                    src: "/images/generated/compressed_WhatsApp-Image-2026-07-18-at-11.45.31-AM.webp",
                    alt: "Azadi Ka Amrit Mahotsav",
                  },
                ].map((logo) => (
                  <div
                    key={logo.alt}
                    className="w-11 h-11 sm:w-[60px] sm:h-[60px] md:w-[72px] md:h-[72px] flex-shrink-0 rounded-xl overflow-hidden bg-white border border-gray-100 flex items-center justify-center p-1 sm:p-1.5 hover:border-primary/20 hover:shadow-sm transition-all duration-300"
                  >
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <SectionTag>Ministry of Defence, Government of India</SectionTag>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <h1 className="font-heading font-black text-[clamp(2rem,4vw,3.2rem)] leading-[1.08] text-secondary tracking-tight mb-4">
                Adventure Training,
                <br />
                <span className="text-primary relative">
                  Redefined
                  <span className="absolute -bottom-1 left-0 w-full h-1.5 bg-primary/15 rounded-full" />
                </span>
              </h1>
            </AnimatedSection>

            <AnimatedSection delay={0.15}>
              <p className="text-gray-500 text-base md:text-lg max-w-lg lg:max-w-none mx-auto lg:mx-0 leading-relaxed mb-6">
                India's premier mountaineering and adventure institute — a 38-acre
                Himalayan campus in Dirang, Arunachal Pradesh offering world-class training across Land, Air & Aqua verticals.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
                <Link
                  to="/courses"
                  className="w-full sm:w-auto px-7 py-3.5 bg-secondary hover:bg-secondary/90 text-white text-sm font-semibold tracking-wide rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-secondary/20 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-center"
                >
                  Explore Courses
                  <i className="ri-arrow-right-line ml-2" />
                </Link>
                <Link
                  to="/about"
                  className="w-full sm:w-auto px-7 py-3.5 border-2 border-gray-200 hover:border-secondary/30 text-secondary text-sm font-semibold tracking-wide rounded-xl transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-center bg-white"
                >
                  About NIMAS
                </Link>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={0.25} className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl relative">
                <div className="grid grid-cols-3 grid-rows-2 gap-1.5 w-full h-full bg-white">
                  {/* -hero variants: this grid displays each image at a small
                      mosaic-cell size, but the plain /images/courses/*.jpg
                      files are full-resolution (1400x1050) originals shared
                      with course detail pages that actually need that size —
                      downloading those here wasted ~1.2MB per pageview
                      (confirmed via PageSpeed Insights' "Improve image
                      delivery" audit, 2026-09-29). These are 640px-wide
                      re-encodes of the same source images, dedicated to this
                      mosaic only. */}
                  <OptimizedImage
                    src="/images/courses/mountaineering-hero.jpg"
                    alt="Mountaineering and ice-craft training at NIMAS"
                    fetchPriority="high"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <OptimizedImage
                    src="/images/courses/mtb-hero.jpg"
                    alt="Mountain biking on Himalayan trails"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <OptimizedImage
                    src="/images/courses/mtb-2-hero.jpg"
                    alt="Group mountain biking expedition"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <OptimizedImage
                    src="/images/courses/paragliding-hero.jpg"
                    alt="Paragliding with NIMAS over Himalayan valleys"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <OptimizedImage
                    src="/images/courses/rafting-hero.jpg"
                    alt="White water rafting adventure at NIMAS"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <OptimizedImage
                    src="/images/courses/scuba-hero.jpg"
                    alt="Scuba diving training underwater"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent pointer-events-none" />
              </div>

              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-lg border border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-green-600 text-lg" />
                </div>
                <div>
                  <p className="text-sm font-bold text-secondary">100% Safety Record</p>
                  <p className="text-xs text-gray-400">Since establishment</p>
                </div>
              </div>

              <div className="absolute -top-5 -right-5 bg-white rounded-2xl p-4 shadow-lg border border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-award-line text-emerald-600 text-lg" />
                </div>
                <div>
                  <p className="text-sm font-bold text-secondary">Govt. Certified</p>
                  <p className="text-xs text-gray-400">MoD Recognized</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        <AnimatedSection delay={0.4} className="mt-12 lg:mt-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className="relative group cursor-default"
                onMouseEnter={() => setHoveredStat(i)}
                onMouseLeave={() => setHoveredStat(null)}
              >
                <div className={`text-center p-4 rounded-2xl transition-all duration-300 ${
                  hoveredStat === i ? "bg-secondary text-white shadow-lg shadow-secondary/20 scale-[1.02]" : "bg-gray-50 text-secondary"
                }`}>
                  <p className={`font-heading font-black text-2xl md:text-3xl mb-0.5 transition-colors duration-300 ${
                    hoveredStat === i ? "text-white" : "text-secondary"
                  }`}>
                    {stat.value}
                  </p>
                  <p className={`text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    hoveredStat === i ? "text-white/60" : "text-gray-400"
                  }`}>
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function CoursesSection({ categories, loading }: { categories: CourseCategoryCard[]; loading: boolean }) {
  return (
    <section className="py-28 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <SectionTag>Our Programs</SectionTag>
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-secondary leading-tight mb-4">
            Choose Your Adventure
          </h2>
          <p className="text-gray-500 text-base max-w-lg mx-auto">
            From Himalayan peaks to underwater depths — courses across three verticals designed to push your limits.
          </p>
        </AnimatedSection>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
                <div className="aspect-[16/10] bg-gray-100 animate-shimmer" />
                <div className="p-5 space-y-3">
                  <div className="w-24 h-5 bg-gray-100 rounded-full animate-shimmer" />
                  <div className="w-3/4 h-6 bg-gray-100 rounded animate-shimmer" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((course, i) => (
              <AnimatedSection key={course.slug} delay={i * 0.08} className="flex">
                <Link
                  to={`/courses/${course.slug}`}
                  className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/20 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 w-full"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-gray-100 flex-shrink-0">
                    <OptimizedImage
                      // Paramotor Trike's source photo is a tall portrait shot —
                      // a separately pre-padded 16:10 landscape version (sky-blue
                      // letterboxing matched to the photo) is used here so the
                      // full trike is visible instead of getting cropped by
                      // object-cover on this card's fixed aspect ratio.
                      src={course.name === "Paramotor Trike" ? "/images/courses/paramotor-trike-home-hero.jpg" : course.image}
                      alt={course.name}
                      loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-lg text-secondary mb-1 group-hover:text-primary transition-colors">
                      {course.name}
                    </h3>
                    <div className="flex items-center justify-end mt-auto pt-4">
                      <div className="flex items-center gap-1.5 text-primary text-sm font-semibold group-hover:gap-2.5 transition-all">
                        <span className="text-xs">View</span>
                        <i className="ri-arrow-right-line text-sm" />
                      </div>
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        )}

        <AnimatedSection className="mt-12 text-center">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-secondary hover:bg-secondary/90 text-white text-sm font-semibold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-secondary/20 hover:-translate-y-0.5 whitespace-nowrap"
          >
            View All Courses
            <i className="ri-arrow-right-line" />
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
}

function WhyChooseSection() {
  const features = [
    {
      icon: "ri-shield-check-line",
      title: "100% Safety Record",
      desc: "Rigorous protocols, certified instructors, and state-of-the-art equipment ensure your safety on every expedition.",
      color: "green",
    },
    {
      icon: "ri-map-pin-line",
      title: "38-Acre Himalayan Campus",
      desc: "Nestled at 1,500m altitude in Dirang, our campus offers world-class training facilities amid breathtaking scenery.",
      color: "amber",
    },
    {
      icon: "ri-award-line",
      title: "Govt. Certified Programs",
      desc: "All courses recognized by the Ministry of Defence with internationally valid certifications upon completion.",
      color: "primary",
    },
    {
      icon: "ri-team-line",
      title: "Expert Instructors",
      desc: "Learn from decorated mountaineers, ex-armed forces personnel, and certified international adventure instructors.",
      color: "secondary",
    },
  ];

  const colorMap: Record<string, { bg: string; text: string; ring: string }> = {
    green: { bg: "bg-green-50", text: "text-green-600", ring: "ring-green-100" },
    amber: { bg: "bg-amber-50", text: "text-amber-600", ring: "ring-amber-100" },
    primary: { bg: "bg-primary/10", text: "text-primary", ring: "ring-primary/20" },
    secondary: { bg: "bg-secondary/5", text: "text-secondary", ring: "ring-secondary/10" },
  };

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <SectionTag>Why NIMAS</SectionTag>
            <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-secondary leading-tight mb-6">
              Where Passion Meets Peak Performance
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-10 max-w-md">
              We go beyond the ordinary to exceed your expectations, ensuring
              that every course is a step closer to the summit of adventure.
            </p>
            <div className="flex items-center gap-4 bg-secondary rounded-2xl p-5 w-fit">
              <Logo width={44} height={44} />
              <div>
                <p className="font-bold text-white text-sm">NIMAS Dirang</p>
                <p className="text-gray-300 text-xs mt-0.5">
                  Ministry of Defence, Govt. of India
                </p>
              </div>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((f, i) => {
              const c = colorMap[f.color];
              return (
                <AnimatedSection key={f.title} delay={i * 0.1}>
                  <div className="group bg-gray-50/70 rounded-2xl p-6 border border-gray-100 hover:bg-white hover:shadow-lg hover:border-gray-200 transition-all duration-300">
                    <div className={`w-11 h-11 ${c.bg} rounded-xl flex items-center justify-center mb-4 ring-1 ${c.ring} group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${f.icon} ${c.text} text-xl`} />
                    </div>
                    <h3 className="font-heading font-bold text-base text-secondary mb-1.5">
                      {f.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {f.desc}
                    </p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function BatchesSection({ courses }: { courses: Course[] }) {
  const [imgErrors, setImgErrors] = useState<Record<number, boolean>>({});
  const { batches: upcomingBatches, loading: batchesLoading, empty: batchesEmpty } = useUpcomingBatches();

  const categoryFallbackImage: Record<string, string> = {
    land: "/images/generated/upcoming-fallback-land-01.jpg",
    aqua: "/images/courses/rafting.jpg",
    aero: "/images/courses/paragliding.jpg",
  };

  return (
    <section className="py-28 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <SectionTag>Limited Seats</SectionTag>
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-secondary leading-tight mb-4">
            Available Batches
          </h2>
          <p className="text-gray-500 text-base max-w-lg mx-auto">
            Reserve your spot early. Our batches fill up fast with adventurers from across India and abroad.
          </p>
        </AnimatedSection>

        {batchesLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 lg:p-7 border border-gray-100 h-24 animate-shimmer" />
            ))}
          </div>
        ) : batchesEmpty ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <i className="ri-calendar-close-line text-3xl text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No listings available for the current Financial Year.</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[720px] overflow-y-auto pr-1">
            {upcomingBatches.map((batch, i) => {
              const course = courses.find((c) =>
                c.slug === batch.courseSlug || batch.courseSlug.startsWith(c.slug.replace('-basic', '').replace('-advanced', '').replace('-intermediate', '').replace('-combined', '').replace('-course', ''))
              );
              const percent = batch.seatsTotal > 0 ? Math.round((batch.seatsBooked / batch.seatsTotal) * 100) : 0;
              const fullyBooked = batch.seatStatus === "fullyBooked";
              const limited = batch.seatStatus === "limited";
              const fallbackImg = categoryFallbackImage[course?.category || "land"];
              const imgSrc = imgErrors[i] ? fallbackImg : (course?.image || fallbackImg);

              return (
                <AnimatedSection key={i} delay={Math.min(i, 8) * 0.06}>
                  <div className={`group bg-white rounded-2xl p-5 lg:p-7 border border-gray-100 hover:border-primary/20 hover:shadow-md transition-all duration-300 ${fullyBooked ? "opacity-70" : ""}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                          <img
                            src={imgSrc}
                            alt={batch.courseName}
                            loading="lazy"
                            onError={() => setImgErrors((prev) => ({ ...prev, [i]: true }))}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div>
                          {batch.serialNo && (
                            <span className="inline-block text-[11px] font-semibold text-primary bg-primary/5 px-2 py-0.5 rounded-full mb-1">
                              {batch.serialNo}
                            </span>
                          )}
                          <h3 className="font-heading font-bold text-base text-secondary">
                            {batch.courseName}
                          </h3>
                        </div>
                      </div>

                      <div className="flex-1 max-w-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500">
                            {batch.seatsBooked} of {batch.seatsTotal} booked
                          </span>
                          <span
                            className={`text-xs font-bold ${
                              fullyBooked ? "text-red-500" : limited ? "text-red-500" : "text-primary"
                            }`}
                          >
                            {fullyBooked ? "Fully Booked" : limited ? "Almost Full!" : `${percent}%`}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              fullyBooked || limited ? "bg-red-400" : "bg-primary"
                            }`}
                            style={{ width: `${Math.min(percent, 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-secondary font-bold text-xl">
                            ₹{batch.price.toLocaleString("en-IN")}
                          </p>
                          <p className="text-gray-500 text-xs">per participant</p>
                        </div>
                        {fullyBooked ? (
                          <button
                            type="button"
                            disabled
                            aria-disabled="true"
                            className="px-5 py-3 bg-gray-200 text-gray-500 text-sm font-semibold rounded-xl whitespace-nowrap cursor-not-allowed"
                          >
                            Fully Booked
                          </button>
                        ) : (
                          <Link
                            to={`/booking/${batch.courseSlug}?from=home`}
                            className="px-5 py-3 bg-secondary hover:bg-secondary/90 text-white text-sm font-semibold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-secondary/20 whitespace-nowrap"
                          >
                            Book Now
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const allImages = [...testimonials, ...testimonials];

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.scrollWidth / allImages.length;
      scrollRef.current.scrollTo({
        left: active * cardWidth,
        behavior: "smooth",
      });
    }
  }, [active]);

  return (
    <section className="py-28 bg-black overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-block text-xs font-semibold text-primary tracking-[0.2em] uppercase mb-5 bg-primary/10 px-4 py-1.5 rounded-full">
            Endorsements
          </span>
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">
            Voices From The Summit
          </h2>
          <p className="text-gray-400 text-base max-w-lg mx-auto">
            Real stories from adventurers who trained with NIMAS and conquered their limits.
          </p>
        </AnimatedSection>

        <AnimatedSection>
          <div className="relative overflow-hidden">
            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {allImages.map((t, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 snap-start w-full md:w-[400px] lg:w-[460px]"
                >
                  <div className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-300">
                    <OptimizedImage
                      src={t.image}
                      alt={`Testimonial from ${t.name}`}
                      loading="lazy"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        <div className="flex justify-center gap-2 mt-10">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === active ? "bg-primary w-8" : "bg-white/20 w-1.5"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}

function RatingStars({ rate }: { rate: number }) {
  return (
    <div className="flex items-center gap-0.5" role="img" aria-label={`${rate} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <i key={i} className={`text-sm ${i < rate ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`} />
      ))}
    </div>
  );
}

function formatFeedbackDate(d: string): string {
  if (!d) return "";
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function FeedbackCard({ f }: { f: FeedbackRow }) {
  return (
    <div className="h-full flex flex-col bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <i className="ri-user-3-fill text-primary text-lg" />
        </div>
        <div>
          <p className="font-heading font-bold text-secondary text-sm">{f.name || "Anonymous"}</p>
          <RatingStars rate={f.experienceRate} />
        </div>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed mt-4 flex-1">
        "{f.feedback}"
      </p>
      <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
        {f.course && <p className="text-xs text-primary font-medium">{f.course}</p>}
        <p className="text-xs text-gray-400">{formatFeedbackDate(f.date)}</p>
      </div>
    </div>
  );
}

function FeedbackSection() {
  const { feedback, loading, error } = useFeaturedFeedback();
  const featured = feedback.slice(0, 9);
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Advances one review card at a time (not a full row of 3) — each card is
  // its own carousel slide, sized to 1/3 width on desktop so 3 stay visible.
  useEffect(() => {
    if (featured.length <= 1) return;
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % featured.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [featured.length]);

  useEffect(() => {
    if (scrollRef.current && featured.length > 0) {
      const cardWidth = scrollRef.current.scrollWidth / featured.length;
      scrollRef.current.scrollTo({ left: active * cardWidth, behavior: "smooth" });
    }
  }, [active, featured.length]);

  if (!loading && !error && featured.length === 0) return null;

  return (
    <section className="py-28 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <SectionTag>Student Feedback</SectionTag>
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-secondary leading-tight mb-4">
            Highly Rated By Our Trainees
          </h2>
          <p className="text-gray-500 text-base max-w-lg mx-auto">
            Real, top-rated feedback submitted directly by students who completed a course with NIMAS.
          </p>
        </AnimatedSection>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-3">
                <div className="w-24 h-4 bg-gray-100 rounded animate-shimmer" />
                <div className="w-full h-4 bg-gray-100 rounded animate-shimmer" />
                <div className="w-3/4 h-4 bg-gray-100 rounded animate-shimmer" />
                <div className="w-1/2 h-4 bg-gray-100 rounded animate-shimmer" />
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-gray-400 text-sm">{error}</p>
        ) : (
          <>
            <AnimatedSection>
              <div className="relative overflow-hidden">
                <div
                  ref={scrollRef}
                  className="flex overflow-x-auto snap-x snap-mandatory gap-6"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  {featured.map((f) => (
                    <div key={f.id} className="flex-shrink-0 w-full md:w-[calc((100%-3rem)/3)] snap-start">
                      <FeedbackCard f={f} />
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {featured.length > 1 && (
              <div className="flex justify-center gap-1 mt-10">
                {featured.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    aria-label={`Show feedback ${i + 1}`}
                    className="flex items-center justify-center w-6 h-6 cursor-pointer"
                  >
                    <span
                      className={`h-1.5 rounded-full transition-all ${
                        i === active ? "bg-primary w-8" : "bg-gray-300 w-1.5"
                      }`}
                    />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function SocialFeedSection() {
  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <SectionTag>Stay Connected</SectionTag>
          </div>
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-secondary leading-tight mb-4">
            Social Media Feed
          </h2>
          <p className="text-gray-500 text-base max-w-lg mx-auto">
            Follow our latest adventures, announcements, and behind-the-scenes moments across social platforms.
          </p>
        </AnimatedSection>

        <AnimatedSection className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href="https://www.instagram.com/nimas_dirang?stkn=MW1kczJxdnA0dW5zdg%3D%3D&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-instagram-line" /> Follow on Instagram
          </a>
          <a
            href="https://www.facebook.com/nimas2018"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-facebook-fill" /> Follow on Facebook
          </a>
          <a
            href="https://x.com/dirangnimas?lang=en"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-twitter-x-fill" /> Follow on X
          </a>
          <a
            href="https://www.youtube.com/@nimasdirang"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-youtube-fill" /> Subscribe on YouTube
          </a>
        </AnimatedSection>
      </div>
    </section>
  );
}

function ILPBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (dismissed) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-500 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="bg-amber-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 overflow-hidden flex-1 min-w-0">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-white text-lg" />
            </div>
            <div className="overflow-hidden">
              <div className="animate-marquee whitespace-nowrap">
                <strong className="font-heading font-bold text-sm tracking-wide">
                  IMPORTANT: &nbsp;ILP (Inner Line Permit) is mandatory for entry into Arunachal Pradesh.
                </strong>
                <span className="mx-4 text-white/70">|</span>
                <span className="text-sm text-white/90">
                  All visitors must obtain a valid ILP before travelling. Apply online at the Arunachal Pradesh eILP portal.
                </span>
                <span className="mx-4 text-white/70">|</span>
                <strong className="font-heading font-bold text-sm tracking-wide">
                  IMPORTANT: &nbsp;ILP (Inner Line Permit) is mandatory for entry into Arunachal Pradesh.
                </strong>
                <span className="mx-4 text-white/70">|</span>
                <span className="text-sm text-white/90">
                  All visitors must obtain a valid ILP before travelling. Apply online at the Arunachal Pradesh eILP portal.
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={() => setDismissed(true)}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Land -> Air -> Aqua vertical grouping for "Our Programs", matching the
// same order used on the /courses page — categories with no entry here sort
// to the end.
const HOME_CATEGORY_ORDER = [
  // Land
  "Mountaineering",
  "Sport Climbing",
  "Mountain Terrain Biking",
  // Air
  "Paragliding",
  "Paramotor",
  "Paramotor Trike",
  // Aqua
  "SCUBA Diving",
  "White Water Rafting",
  "White Water Kayaking",
];
const homeCategoryRank = (category: string): number => {
  const idx = HOME_CATEGORY_ORDER.indexOf(category);
  return idx === -1 ? HOME_CATEGORY_ORDER.length : idx;
};
const HOME_KNOWN_CATEGORIES = new Set(HOME_CATEGORY_ORDER.map((c) => c.toLowerCase()));

export default function Home() {
  const { courses } = useCourses();
  const { categories: rawCategories, loading: categoriesLoading } = useCourseCategories();
  // templateID 2 ("Courselist") occasionally has stray rows that aren't real
  // categories (e.g. a sub-course name like "Basic Mountaineering Course
  // (BMC)") — filter to the known real category names, same as /courses.
  const categories = useMemo(
    () => rawCategories.filter((c) => HOME_KNOWN_CATEGORIES.has(c.category.trim().toLowerCase())),
    [rawCategories]
  );
  // All categories now render in CoursesSection (no preview cap) — Paramotor
  // Trike is inserted right after Paramotor (its own vertical slot) rather
  // than appended at the end, so the Land -> Air -> Aqua order holds.
  const categoriesWithTrike = useMemo(() => {
    const sorted = [...categories].sort((a, b) => homeCategoryRank(a.category) - homeCategoryRank(b.category));
    const paramotorIdx = sorted.findIndex((c) => c.category === "Paramotor");
    return paramotorIdx === -1
      ? withParamotorTrike(sorted)
      : [...sorted.slice(0, paramotorIdx + 1), PARAMOTOR_TRIKE_CARD, ...sorted.slice(paramotorIdx + 1)];
  }, [categories]);
  useSEO({
    title: "NIMAS Dirang — National Institute of Mountaineering & Adventure Sports",
    description: "India's premier national institute for mountaineering and adventure sports training under the Ministry of Defence. World-class courses in mountaineering, paragliding, scuba diving, white water rafting, mountain biking and paramotor across Land, Air and Aqua verticals in Arunachal Pradesh.",
    path: "/",
  });
  return (
    <div>
      <HomeSchemaJSON />
      <HeroSection />
      <CoursesSection categories={categoriesWithTrike} loading={categoriesLoading} />
      <WhyChooseSection />
      {/* Endorsements section — temporarily disabled */}
      {/* <TestimonialsSection /> */}
      <FeedbackSection />
      <SocialFeedSection />
      <BatchesSection courses={courses} />
      <ILPBanner />
    </div>
  );
}